import React, { FC, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { LibraryBooks } from '@material-ui/icons';
import EditIcon from '@material-ui/icons/Edit';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import { getCustomActivities, updatePermitActivity } from '../../api/activities';
import { RootState } from '../../redux/root';
import PaginationMobile from './PaginationMobile';
import { setRowsCount } from '../../redux/slices/paginationSlice';
import useTablePage from '../../hooks/useTablePage';
import { ModalDefaultDraggable } from '../../molecules';
import { Loader } from '../../atoms';
import PermitActivitiesForm from '../../forms/PermitActivitiesForm';
import useManuallyUpdate from '../../hooks/useManuallyUpdate';
import useFilterActions from './useFilterActions';
import ModalInfo from '../../molecules/ModalInfo';
import MobileDocumentsDetails from './Documents/MobileDocumentsDetails/MobileDocumentsDetails';

const formatFollowUp = (raw: any): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const PendingReviewsMobile: FC = () => {
  const { pageSize, pageIndex } = useSelector((state: RootState) => state.pagination);
  const currentUserId = useSelector((state: RootState) => state.me.currentUserId);
  const [records, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const globallyFiltered = useFilterActions(records);
  const filteredRecords = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return globallyFiltered;
    return globallyFiltered.filter((r: any) =>
      `${r?.projectName || ''}`.toLowerCase().includes(q),
    );
  }, [globallyFiltered, searchTerm]);
  const [initialValues, setInitialValues] = useState([]);
  const [permit, setPermit] = useState<number>(-1);
  const [errors] = useState<any>(null);
  const [documents, showDocuments] = React.useState(null);
  const [tableManuallyUpdate, updateTable] = useManuallyUpdate();
  const dispatch = useDispatch();
  const {
    isModalOpen,
    modalSuccess,
    modalError,
    modalTitle,
    modalErrorTitle,
    createOrUpdate,
    handleEdit,
    handleCloseModalInfo,
  } = useTablePage({
    updateEndpoint: updatePermitActivity,
    modalUpdateTitle: 'Update Review',
  });

  useEffect(() => {
    if (currentUserId > 0) {
      setIsLoading(true);
      getCustomActivities({
        is_deleted: false,
        limit: pageSize,
        offset: pageIndex,
        inspector_id: currentUserId,
        event_type: 'Activity',
        activity_type_name: 'Reviews',
      })
        .then(result => {
          setRecords(result.data.results);
          dispatch(setRowsCount(result.data.results.length));
        })
        .finally(() => setIsLoading(false));
    }
  }, [currentUserId, pageSize, pageIndex, modalSuccess, modalError]);

  const handleEditModal = data => {
    if (data) {
      setPermit(data?.permitId);
      setInitialValues(data);
      handleEdit(data);
    }
  };

  const closeInfoModal = () => {
    setInitialValues(null);
    showDocuments(false);
    updateTable();
    handleCloseModalInfo();
  };

  const viewComments = (record: any): void => {
    if (!record.reviewTypeComment) {
      return;
    }
    showDocuments(record);
  };

  return (
    <>
      <SearchBar>
        <SearchIconWrap>
          <SearchIcon style={{ fontSize: 18 }} />
        </SearchIconWrap>
        <SearchInput
          type="search"
          placeholder="Search project name"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          autoComplete="off"
        />
        {searchTerm && (
          <ClearBtn type="button" aria-label="Clear search" onClick={() => setSearchTerm('')}>
            <CloseIcon style={{ fontSize: 16 }} />
          </ClearBtn>
        )}
      </SearchBar>
      <ReviewPageContainer>
        {isLoading && filteredRecords.length === 0 && (
          <LoaderWrapper>
            <Loader size={36} thickness={3.5} />
            <LoaderLabel>Loading…</LoaderLabel>
          </LoaderWrapper>
        )}
        {!isLoading && filteredRecords.length === 0 && (
          <EmptyState>{searchTerm ? 'No matches.' : 'No reviews found.'}</EmptyState>
        )}
        {filteredRecords.map((record, index) => {
          const projectLine = [record?.projectName, record?.permitName].filter(Boolean).join(' / ') || '—';
          const subLineBits = [record?.reviewType, record?.projectJurisdiction, formatFollowUp(record?.followUp)].filter(Boolean);
          const hasComments = !!record?.reviewTypeComment;
          return (
            <Row
              key={record?.id ?? index}
              $isEmergency={!!record?.isEmergency}
              onClick={() => handleEditModal(record)}
            >
              <Main>
                <Title title={projectLine}>{projectLine}</Title>
                {subLineBits.length > 0 && (
                  <Sub title={subLineBits.join(' · ')}>
                    {subLineBits.map((bit, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <Dot>·</Dot>}
                        <span>{bit}</span>
                      </React.Fragment>
                    ))}
                  </Sub>
                )}
              </Main>
              <ActionGroup onClick={e => e.stopPropagation()}>
                <IconBtn
                  type="button"
                  aria-label="Edit review"
                  onClick={() => handleEditModal(record)}
                >
                  <EditIcon style={{ fontSize: 18 }} />
                </IconBtn>
                <IconBtn
                  type="button"
                  $disabled={!hasComments}
                  aria-label="View comments"
                  onClick={() => viewComments(record)}
                  disabled={!hasComments}
                >
                  <LibraryBooks style={{ fontSize: 18 }} />
                </IconBtn>
              </ActionGroup>
            </Row>
          );
        })}
      </ReviewPageContainer>
      <PaginationMobile />
      {isModalOpen && initialValues && (
        <ModalDefaultDraggable title={modalTitle} onClose={closeInfoModal}>
          <PermitActivitiesForm
            onSubmit={createOrUpdate}
            initialValues={initialValues}
            modalError={modalError}
            addButtonText="Create"
            updateButtonText="Update"
            initialErrors={errors}
            permit={permit}
          />
        </ModalDefaultDraggable>
      )}

      {documents && (
        <ModalDefaultDraggable title="Permit Documents" onClose={closeInfoModal} extraWide>
          <MobileDocumentsDetails documentId={documents.id} permitId={documents.permit} />
        </ModalDefaultDraggable>
      )}

      {(modalSuccess || modalError) && (
        <ModalInfo timeToClose={5000} isError={modalError} onClose={closeInfoModal} errorMsg={modalErrorTitle} />
      )}
    </>
  );
};

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

export const ReviewPageContainer = styled.div`
  display: none;

  @media only screen and (max-width: 1366px) {
    display: block;
    height: 76vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 8px;
    font-family: ${FONT_STACK};
  }

  @media only screen and (max-width: 540px) {
    height: 74vh;
    padding: 0 4px;
  }
`;

const SearchBar = styled.div`
  display: none;
  position: relative;
  align-items: center;

  @media only screen and (max-width: 1366px) {
    display: flex;
    margin: 0 8px 8px;
    font-family: ${FONT_STACK};
  }

  @media only screen and (max-width: 540px) {
    margin: 0 4px 8px;
  }
`;

const SearchIconWrap = styled.span`
  position: absolute;
  left: 10px;
  display: inline-flex;
  align-items: center;
  color: var(--neutral-500);
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 38px;
  padding: 0 34px 0 34px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--mainText);
  font-family: inherit;
  font-size: 0.875rem;
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &::placeholder {
    color: var(--neutral-400);
  }

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const ClearBtn = styled.button`
  position: absolute;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  &:hover {
    background: var(--neutral-100);
    color: var(--mainText);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const Row = styled.div<{ $isEmergency: boolean }>`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-left: 3px solid
    ${({ $isEmergency }) => ($isEmergency ? 'var(--danger-500, #dc2626)' : 'transparent')};
  border-radius: 8px;
  box-shadow: var(--shadow-xs);
  margin-bottom: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--neutral-50);
  }

  &:active {
    background: var(--neutral-100);
  }
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Sub = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: var(--neutral-500);
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const Dot = styled.span`
  color: var(--neutral-400);
  flex-shrink: 0;
`;

const ActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const IconBtn = styled.button<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: ${({ $disabled }) => ($disabled ? 'var(--neutral-300)' : 'var(--neutral-600)')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
  padding: 0;

  &:hover:not(:disabled) {
    background: var(--primary-50);
    color: var(--primary-600);
    border-color: var(--primary-100, #cfe6f3);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 16px;
`;

const LoaderLabel = styled.div`
  font-size: 0.875rem;
  color: var(--neutral-500);
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

export default PendingReviewsMobile;
